import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SentimentDissatisfiedRoundedIcon from "@mui/icons-material/SentimentDissatisfiedRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import ScratchCanvas from "./ScratchCanvas";
import ScratchCardCover from "./ScratchCardCover";
import { buildCardDesigns } from "./scratchCardDesigns";
import confetti from "canvas-confetti";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { api_url, img_url } from "../../../apiUtils";
import { useAppStore } from "../../../appStore";
import { getDeviceId } from "../../utils/DeviceId";
import socket from "../../../socketClient";
import { toast } from "react-toastify";

const CARD_SIZE = 160;

// A group can hold up to ~10k cards; rendering them all at once bogs the
// customer app down. Render a page at a time and grow as the user scrolls.
const CARD_PAGE_SIZE = 25;

const TICKET_COLORS = [
  { from: "#202963", to: "#111744", stub: "#f5c400" },
  { from: "#273270", to: "#151b50", stub: "#ffd500" },
  { from: "#1d285f", to: "#0d123b", stub: "#e9b900" },
].map((c) => ({ ...c, main: `linear-gradient(135deg, ${c.from}, ${c.to})` }));

const ScratchAndWinGame = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const scratchCardSize = isMobile
    ? typeof window !== "undefined" && window.innerWidth < 360
      ? 220
      : typeof window !== "undefined" && window.innerWidth < 400
        ? 230
        : 245
    : CARD_SIZE;
  const {
    walletBalance,
    setWalletBalance,
    autoGameBalance,
    setAutoGameBalance,
    profileData,
    setProfileData,
    setAddPointOpen,
    setPreferredDepositWalletType,
  } = useAppStore();
  const canUseAutoGameWallet = Boolean(profileData?.canUseAutoGameWallet);
  const playableBalance = canUseAutoGameWallet
    ? autoGameBalance
    : walletBalance;
  const setPlayableBalance = useCallback(
    (value) => {
      if (canUseAutoGameWallet) {
        setAutoGameBalance(value);
      } else {
        setWalletBalance(value);
      }
    },
    [canUseAutoGameWallet, setAutoGameBalance, setWalletBalance],
  );

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [ticketsRemaining, setTicketsRemaining] = useState(0);
  const [visibleCount, setVisibleCount] = useState(CARD_PAGE_SIZE);
  const [selectedCover, setSelectedCover] = useState(null);
  const cardDesigns = useMemo(
    () =>
      buildCardDesigns(
        selectedGroup?.numberOfTickets || 0,
        selectedGroup?.cardPhotos || [],
      ),
    [selectedGroup?.numberOfTickets, selectedGroup?.cardPhotos],
  );
  const getPhotoUrl = (photo) =>
    photo?.url ? new URL(photo.url, img_url).href : null;

  const [scratchModalOpen, setScratchModalOpen] = useState(false);
  const [scratching, setScratching] = useState(false);
  const [scratchResult, setScratchResult] = useState(null);
  const [scratchRevealed, setScratchRevealed] = useState(false);
  const [revealPending, setRevealPending] = useState(false);
  const [scratchStarted, setScratchStarted] = useState(false);
  const [reservationId, setReservationId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const confettiCanvasRef = useRef(null);
  const confettiTimerRef = useRef(null);
  const commitPromiseRef = useRef(null);
  const scratchActiveRef = useRef(false);
  const reservationIdRef = useRef(null);
  const balanceBeforeScratchRef = useRef(0);

  useEffect(() => {
    if (profileData?.canUseAutoGameWallet !== undefined) return;

    const token = sessionStorage.getItem("accesstoken");
    if (!token) return;

    let cancelled = false;
    axios
      .get(`${api_url}auth/profile`, {
        headers: { accesstoken: token },
      })
      .then((response) => {
        if (!cancelled && response.data?.userProfile) {
          setProfileData(response.data.userProfile);
        }
      })
      .catch((error) => {
        console.error("Unable to fetch profile for auto game wallet:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [profileData?.canUseAutoGameWallet, setProfileData]);

  const fetchAutoGamePlayableBalance = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("accesstoken");
      const response = await axios.get(`${api_url}wallet/balance`, {
        headers: { accesstoken: token },
      });
      const wallet = response.data?.walletPoint;
      const playableBalance = canUseAutoGameWallet
        ? wallet?.autoGameBalance
        : wallet?.balance;
      if (Number.isFinite(Number(playableBalance))) {
        const numericBalance = Number(playableBalance);
        setPlayableBalance(numericBalance);
        return numericBalance;
      }
    } catch (error) {
      console.error("Unable to fetch auto game balance:", error);
    }
    return 0;
  }, [canUseAutoGameWallet, setPlayableBalance]);

  useEffect(() => {
    fetchAutoGamePlayableBalance();
  }, [fetchAutoGamePlayableBalance]);

  const selectedGroupIdRef = useRef(null);
  useEffect(() => {
    selectedGroupIdRef.current = selectedGroup?._id || null;
  }, [selectedGroup]);

  useEffect(() => {
    // Leaving the page (route change, back button, tab close) with a card
    // still open hands it back immediately, so the ticket returns to the
    // pool for other players instead of sitting out until the cron sweep.
    // No money is at stake here — the charge only happens at commit.
    //
    // The guard is "reserved but no commit started", not "started
    // scratching": tapping a card and backing straight out is the common
    // case and left the ticket stranded under the old check. Once
    // handleScratchComplete has fired, commitPromiseRef is set and we must
    // NOT cancel — that request is claiming a prize.
    const releaseInterruptedScratch = () => {
      if (!reservationIdRef.current || commitPromiseRef.current) return;

      // Capture before clearing: clearing first makes this idempotent if
      // both the beforeunload handler and the unmount cleanup fire.
      const abandonedId = reservationIdRef.current;
      scratchActiveRef.current = false;
      reservationIdRef.current = null;
      fetch(`${api_url}game/scratch-and-win/cancel`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          accesstoken: sessionStorage.getItem("accesstoken"),
        },
        body: JSON.stringify({ reservationId: abandonedId }),
      }).catch(() => {});
    };

    window.addEventListener("beforeunload", releaseInterruptedScratch);
    return () => {
      window.removeEventListener("beforeunload", releaseInterruptedScratch);
      releaseInterruptedScratch();
    };
  }, []);

  // Other customers can be scratching cards from the same groups at the same
  // time, so ticketsRemaining is kept in sync via a server broadcast instead
  // of only updating after this device's own plays.
  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }

    const handleGroupUpdated = ({
      groupId,
      ticketsRemaining: updatedRemaining,
    }) => {
      setGroups((prev) =>
        prev
          .map((group) =>
            String(group._id) === String(groupId)
              ? { ...group, ticketsRemaining: updatedRemaining }
              : group,
          )
          .filter((group) => group.ticketsRemaining > 0),
      );

      if (
        selectedGroupIdRef.current &&
        String(selectedGroupIdRef.current) === String(groupId)
      ) {
        setTicketsRemaining(updatedRemaining);
      }
    };

    const handlePhotosUpdated = ({ groupId, cardPhotos }) => {
      setGroups((previous) =>
        previous.map((group) =>
          String(group._id) === String(groupId)
            ? { ...group, cardPhotos }
            : group,
        ),
      );
      setSelectedGroup((group) =>
        group && String(group._id) === String(groupId)
          ? { ...group, cardPhotos }
          : group,
      );
    };
    const refreshPhotos = async () => {
      try {
        const { data } = await axios.get(
          `${api_url}game/scratch-and-win/groups`,
          {
            headers: { accesstoken: sessionStorage.getItem("accesstoken") },
          },
        );
        (data.data || []).forEach((group) =>
          handlePhotosUpdated({
            groupId: group._id,
            cardPhotos: group.cardPhotos || [],
          }),
        );
      } catch (error) {
        console.error("Unable to refresh scratch card photos:", error);
      }
    };
    socket.on("scratchAndWin:groupUpdated", handleGroupUpdated);
    socket.on("scratchAndWin:photosUpdated", handlePhotosUpdated);
    socket.on("connect", refreshPhotos);
    return () => {
      socket.off("scratchAndWin:groupUpdated", handleGroupUpdated);
      socket.off("scratchAndWin:photosUpdated", handlePhotosUpdated);
      socket.off("connect", refreshPhotos);
    };
  }, []);

  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await axios.get(`${api_url}game/scratch-and-win/groups`, {
        headers: { accesstoken: sessionStorage.getItem("accesstoken") },
      });
      setGroups(res?.data?.data || []);
    } catch (error) {
      console.error("Failed to load scratch and win groups:", error);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleSelectGroup = (group) => {
    setSelectedGroup({ ...group, cardPhotos: [...(group.cardPhotos || [])] });
    setTicketsRemaining(group.ticketsRemaining);
    setVisibleCount(CARD_PAGE_SIZE);
  };

  // Grow the visible window as the user nears the bottom of the card list.
  const handleCardScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
      setVisibleCount((prev) =>
        prev >= ticketsRemaining ? prev : prev + CARD_PAGE_SIZE,
      );
    }
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
  };

  const handleBackToMenu = async () => {
    try {
      await axios.post(
        `${api_url}game/extra-game/end-session`,
        {
          gameId: "scratch-and-win",
          deviceId: getDeviceId(),
          sessionToken: sessionStorage.getItem("sessionId"),
        },
        {
          headers: { accesstoken: sessionStorage.getItem("accesstoken") },
        },
      );
    } catch (error) {
      console.error("Failed to end session on back", error);
    } finally {
      navigate("/home/menus");
    }
  };

  // Clicking a card only reserves it: the ticket/card is pulled from the
  // pool for everyone right away, but no money moves and the outcome isn't
  // final until the user finishes scratching (commit) or backs out (cancel).
  //
  // The win/lose outcome is decided server-side here but deliberately
  // withheld from the response. Nothing on this client — network payload,
  // component state or DOM — knows whether the card won until commit, which
  // is what stops a player cancelling out of a loss they've already seen.
  const handlePlayCard = async (cover = null) => {
    if (scratching || !selectedGroup || ticketsRemaining <= 0) return;
    const latestBalance = await fetchAutoGamePlayableBalance();
    const ticketPrice = Number(selectedGroup.ticketPrice || 0);
    if (Number(latestBalance) < ticketPrice) {
      toast.error(
        `Insufficient Auto Game Balance. Please add points to Auto Game Wallet.`,
      );
      return;
    }

    // Ticket value is ₹${ticketPrice.toLocaleString(
    //       "en-IN",
    //     )}, available balance is ₹${Number(latestBalance || 0).toLocaleString(
    //       "en-IN",
    //       { maximumFractionDigits: 2 },
    //     )}.
    setScratching(true);
    setSelectedCover(cover);
    setScratchResult(null);
    setScratchRevealed(false);
    setRevealPending(false);
    setScratchStarted(false);
    commitPromiseRef.current = null;
    scratchActiveRef.current = false;

    try {
      const res = await axios.post(
        `${api_url}game/scratch-and-win/reserve`,
        { ticketPrice: selectedGroup.ticketPrice },
        { headers: { accesstoken: sessionStorage.getItem("accesstoken") } },
      );
      const data = res.data;
      setReservationId(data.reservationId);
      reservationIdRef.current = data.reservationId;
      setScratchModalOpen(true);
      // Absolute count from the server, not a relative decrement: the
      // socket broadcast for this same reservation can arrive before this
      // HTTP response and already apply the authoritative value, so
      // decrementing again here on top of that would double-deduct.
      setTicketsRemaining(data.ticketsRemaining);
      setGroups((prev) =>
        prev.map((group) =>
          group._id === selectedGroup._id
            ? { ...group, ticketsRemaining: data.ticketsRemaining }
            : group,
        ),
      );
    } catch (error) {
      console.error("Scratch and Win reserve failed:", error);
      setScratchModalOpen(false);
      toast.error(
        error?.response?.data?.message ||
          "Could not play this card. Please try again.",
      );
    } finally {
      setScratching(false);
    }
  };

  // fetch with keepalive rather than axios: a keepalive request survives
  // page unload, so scratching and instantly hitting refresh still lands the
  // commit instead of leaving the card stranded for the cron sweep.
  const postCommit = (id) => {
    const pending = fetch(`${api_url}game/scratch-and-win/commit`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        accesstoken: sessionStorage.getItem("accesstoken"),
      },
      body: JSON.stringify({ reservationId: id }),
    }).then(async (response) => {
      // fetch only rejects on network failure, so surface a 4xx/5xx here.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Could not finalize this card.");
      }
      return data;
    });
    // Mark it handled now; the reveal awaits the same promise and deals with
    // the failure there. Without this a slow failure would log an unhandled
    // rejection before the reveal finishes.
    pending.catch(() => {});
    return pending;
  };

  // First contact IS the commit. Firing it on the first rub rather than
  // when the card is fully cleared is the point: from this moment the card
  // is charged and resolved server-side, so refreshing, hitting back or
  // killing the tab can't walk away from a card already scratched into.
  // Setting commitPromiseRef also stops the unload handler cancelling it.
  const handleScratchStart = () => {
    if (scratchActiveRef.current || commitPromiseRef.current || !reservationId)
      return;

    const balanceBeforePlay = Number(playableBalance || 0);
    balanceBeforeScratchRef.current = balanceBeforePlay;
    scratchActiveRef.current = true;
    setScratchStarted(true);
    setPlayableBalance(balanceBeforePlay - Number(selectedGroup.ticketPrice));

    commitPromiseRef.current = postCommit(reservationId);
  };

  // Fires only once a win is confirmed by the server.
  const celebrateWin = () => {
    if (!confettiCanvasRef.current) return;
    const modalConfetti = confetti.create(confettiCanvasRef.current, {
      resize: true,
      useWorker: true,
    });
    const colors = ["#f5b700", "#ffd65a", "#202963", "#ffffff"];
    const celebrate = () => {
      [
        { x: 0.05, angle: 62 },
        { x: 0.95, angle: 118 },
      ].forEach(({ x, angle }) => {
        modalConfetti({
          particleCount: 6,
          angle,
          spread: 48,
          startVelocity: 21,
          origin: { x, y: 0.74 },
          colors,
          scalar: 0.68,
          gravity: 0.76,
          decay: 0.93,
          ticks: 200,
        });
        modalConfetti({
          particleCount: 4,
          angle,
          spread: 42,
          startVelocity: 23,
          origin: { x, y: 0.74 },
          colors,
          scalar: 1.22,
          gravity: 0.72,
          decay: 0.935,
          ticks: 215,
        });
      });
    };
    celebrate();
    confettiTimerRef.current = setInterval(celebrate, 440);
    setTimeout(() => {
      clearInterval(confettiTimerRef.current);
      confettiTimerRef.current = null;
    }, 3200);
  };

  // The cover has finished animating away. The commit was fired on tap, so
  // this only waits for that response and paints it: the commit response is
  // still the first moment this client learns the outcome, which is what
  // stops a player reading the result out of the network tab or the DOM.
  const handleScratchComplete = async () => {
    // Clear the in-progress latch FIRST, before any early return. Returning
    // ahead of this used to leave scratchActiveRef stuck true, which made
    // handleScratchStart bail on every later card: no commit was fired, so
    // this handler bailed too, and the dialog could not be closed because the
    // X hides while a scratch is in progress. One bad card killed the rest.
    scratchActiveRef.current = false;

    // The commit normally fires on first contact. If it somehow didn't, fire
    // it now rather than leaving a fully scratched card with no result — the
    // reservation is still pending server-side, so committing is correct.
    let pending = commitPromiseRef.current;
    if (!pending) {
      if (!reservationId) {
        handleCloseScratchModal();
        return;
      }
      pending = postCommit(reservationId);
      commitPromiseRef.current = pending;
    }
    setRevealPending(true);

    try {
      const { didWin, winAmount, balance } = await pending;

      setScratchResult({ didWin: !!didWin, winAmount: Number(winAmount) || 0 });
      setScratchRevealed(true);
      setRevealPending(false);
      if (Number.isFinite(Number(balance))) {
        setPlayableBalance(Number(balance));
      }
      if (didWin) {
        celebrateWin();
      }
    } catch (error) {
      console.error("Scratch and Win commit failed:", error);
      setRevealPending(false);
      // Nothing was charged: the server returns the ticket when a commit
      // fails, so roll the optimistic deduction back.
      setPlayableBalance(balanceBeforeScratchRef.current);
      handleCloseScratchModal();
      alert(
        error?.message ||
          "Could not finalize this card. Your ticket has been returned.",
      );
    }
  };

  // Backed out before finishing the scratch (mistaken click, changed mind):
  // void the reservation and refund the ticket price. This is safe to offer
  // freely only because the outcome is still unknown to this client — the
  // player is forfeiting blind, not dodging a loss they've already seen.
  const handleCancelReservation = async () => {
    if (!reservationId || cancelling) return;
    scratchActiveRef.current = false;
    setCancelling(true);
    try {
      const res = await axios.post(
        `${api_url}game/scratch-and-win/cancel`,
        { reservationId },
        { headers: { accesstoken: sessionStorage.getItem("accesstoken") } },
      );
      if (Number.isFinite(Number(res.data.balance))) {
        setPlayableBalance(Number(res.data.balance));
      }
      const restoredRemaining = res.data.ticketsRemaining;
      if (typeof restoredRemaining === "number") {
        setTicketsRemaining(restoredRemaining);
        setGroups((prev) =>
          prev.map((group) =>
            group._id === selectedGroup._id
              ? { ...group, ticketsRemaining: restoredRemaining }
              : group,
          ),
        );
      }
      handleCloseScratchModal();
    } catch (error) {
      console.error("Scratch and Win cancel failed:", error);
      alert(
        error?.response?.data?.message ||
          "Could not cancel this card. Please try again.",
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleCloseScratchModal = () => {
    if (confettiTimerRef.current) {
      clearInterval(confettiTimerRef.current);
      confettiTimerRef.current = null;
    }
    setScratchModalOpen(false);
    setScratchResult(null);
    setScratchRevealed(false);
    setRevealPending(false);
    setScratchStarted(false);
    setReservationId(null);
    scratchActiveRef.current = false;
    reservationIdRef.current = null;
    commitPromiseRef.current = null;
  };

  const handleScratchDialogClose = () => {
    if (cancelling || revealPending) return;
    if (scratchRevealed) {
      handleCloseScratchModal();
      return;
    }
    if (scratchStarted) return;
    handleCancelReservation();
  };

  if (loadingGroups) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  // Screen 1: choose a ticket price group
  if (!selectedGroup) {
    return (
      <Box sx={{ width: "100%", pb: 5 }}>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: 3, md: 5 },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1.45, md: 2 },
            mb: 2.25,
            minHeight: { xs: 132, sm: "auto" },
            color: "#fff",
            backgroundImage: {
              xs: "radial-gradient(circle at 0% 50%, rgba(255,213,0,.14), transparent 30%), radial-gradient(circle at 100% 50%, rgba(255,213,0,.12), transparent 32%), linear-gradient(125deg, #303a78 0%, #242e69 58%, #1b2458 100%)",
              sm: "radial-gradient(circle at 0% 50%, rgba(255,213,0,.17), transparent 28%), radial-gradient(circle at 100% 50%, rgba(255,213,0,.2), transparent 30%), linear-gradient(125deg, #202963 0%, #111744 58%, #080d31 100%)",
            },
            backgroundSize: "auto",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            border: "1px solid rgba(255,213,0,.25)",
            boxShadow: "0 16px 38px rgba(17, 23, 68, .22)",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              display: "block",
              opacity: { xs: 0.1, sm: 0.22 },
              backgroundImage:
                "radial-gradient(circle, #fff 1px, transparent 1.5px)",
              backgroundSize: "22px 22px",
              maskImage: "linear-gradient(90deg, transparent 35%, black)",
            },
          }}
        >
          <AutoAwesomeRoundedIcon
            aria-hidden="true"
            sx={{
              position: "absolute",
              zIndex: 0,
              display: "block",
              right: { xs: 42, sm: 92 },
              top: { xs: 12, sm: 10 },
              fontSize: { xs: 17, sm: 23 },
              color: "#ffe878",
              opacity: { xs: 0.5, sm: 0.68 },
              filter: "drop-shadow(0 3px 7px rgba(255,213,0,.26))",
              pointerEvents: "none",
            }}
          />
          <AutoAwesomeRoundedIcon
            aria-hidden="true"
            sx={{
              position: "absolute",
              zIndex: 0,
              display: "block",
              right: { xs: 10, sm: 30 },
              bottom: { xs: 8, sm: 10 },
              fontSize: { xs: 13, sm: 18 },
              color: "#f5c400",
              opacity: { xs: 0.42, sm: 0.58 },
              transform: "rotate(14deg)",
              filter: "drop-shadow(0 2px 6px rgba(255,213,0,.22))",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.15, sm: 3 },
            }}
          >
            <Box display="flex" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.2}>
                <Box
                  onClick={handleBackToMenu}
                  aria-label="Back to games"
                  sx={{
                    width: 32,
                    height: 32,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,.16)",
                    boxShadow: "none",
                    transition: ".2s",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,.25)",
                      transform: "translateX(-2px)",
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 20, sm: 25 },
                      fontWeight: 900,
                      lineHeight: 1.1,
                      textShadow: {
                        xs: "0 3px 10px rgba(0,0,0,.22)",
                        sm: "none",
                      },
                    }}
                  >
                    Scratch & Win
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      opacity: 0.82,
                      fontSize: { xs: 10, sm: 12 },
                      textShadow: {
                        xs: "0 2px 8px rgba(0,0,0,.22)",
                        sm: "none",
                      },
                    }}
                  >
                    Pick a ticket, reveal your prize, win instantly.
                  </Typography>
                </Box>
              </Box>
            </Box>
            {canUseAutoGameWallet && (
              <Box
                onClick={() => {
                  setPreferredDepositWalletType("autoGame");
                  setAddPointOpen(true);
                }}
                sx={{
                  ml: { xs: 0, sm: "auto" },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.05, sm: 1.1 },
                  px: { xs: 1.05, sm: 1.15 },
                  py: { xs: 0.8, sm: 0.75 },
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 286 },
                  justifyContent: "space-between",
                  borderRadius: { xs: 2.6, sm: 2.35 },
                  border: "1px solid rgba(255,255,255,0.24)",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.1))",
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.2), 0 12px 24px rgba(5,8,35,0.18)",
                  backdropFilter: "blur(8px)",
                  cursor: "pointer",
                  transition: "transform 160ms ease, background 160ms ease",
                  "&:active": {
                    transform: "scale(0.985)",
                  },
                  mt: 1,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 32, sm: 42 },
                    height: { xs: 32, sm: 42 },
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: { xs: 2.2, sm: 2 },
                    color: "#1b2055",
                    background: "#ffd80a",
                    boxShadow: "0 8px 16px rgba(0,0,0,.16)",
                  }}
                >
                  <AccountBalanceWalletRoundedIcon
                    sx={{ fontSize: { xs: 24, sm: 23 } }}
                  />
                </Box>
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    gap: { xs: 0.75, sm: 1.25 },
                    flex: { xs: 1, sm: "0 0 auto" },
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.82)",
                      fontSize: { xs: 10, sm: 12 },
                      fontWeight: 800,
                      lineHeight: 1,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      letterSpacing: 0,
                      flexShrink: 0,
                    }}
                  >
                    Auto Game Balance
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0,
                      color: "#fff",
                      fontSize: { xs: 18, sm: 24 },
                      fontWeight: 950,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: { xs: 0.15, sm: 0.25 },
                    }}
                  >
                    <CurrencyRupeeRoundedIcon
                      sx={{
                        fontSize: { xs: 17, sm: 22 },
                        strokeWidth: 1.5,
                      }}
                    />
                    <Box component="span">
                      {Number(playableBalance || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </Box>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "block",
                    width: "1px",
                    alignSelf: "stretch",
                    background: "rgba(255,255,255,.18)",
                    mx: 0.25,
                  }}
                />
                <ChevronRightRoundedIcon
                  sx={{
                    display: "block",
                    color: "rgba(255,255,255,.82)",
                    fontSize: { xs: 25, sm: 28 },
                    flexShrink: 0,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ px: { xs: 0.5, sm: 1 } }}>
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 22 },
              fontWeight: 850,
              color: "#24133b",
            }}
          >
            Choose your lucky ticket
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.4, mb: 2.5, fontSize: 13 }}
          >
            Select a ticket value to start scratching.
          </Typography>

          {groups.length === 0 ? (
            <Box
              sx={{
                py: 7,
                textAlign: "center",
                borderRadius: 4,
                bgcolor: "#faf8fd",
                border: "1px dashed #d8cbea",
              }}
            >
              <ConfirmationNumberRoundedIcon
                sx={{ fontSize: 42, color: "#b7a6c9" }}
              />
              <Typography fontWeight={700} color="text.secondary" mt={1}>
                No scratch cards are available right now
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                Please check again soon.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, md: 2.5 }}>
              {groups.map((group, index) => {
                const colors = TICKET_COLORS[index % TICKET_COLORS.length];
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={group._id}>
                    <Box
                      onClick={() => handleSelectGroup(group)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        minHeight: 128,
                        boxShadow: "0 10px 25px rgba(42, 19, 67, .16)",
                        borderRadius: 3,
                        transition: "transform .25s ease, box-shadow .25s ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: "0 18px 35px rgba(74, 24, 107, .25)",
                          "& .ticket-arrow": { transform: "translateX(3px)" },
                          "& .ticket-main::after": { right: 150 },
                        },
                      }}
                    >
                      <Box
                        className="ticket-main"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          background: colors.main,
                          borderRadius: 3,
                          color: "#fff",
                          py: 2.2,
                          px: 2.2,
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.16,
                            backgroundImage:
                              "radial-gradient(circle, white 1px, transparent 1.5px)",
                            backgroundSize: "15px 15px",
                            maskImage:
                              "linear-gradient(110deg, black, transparent 58%)",
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 90,
                            height: 180,
                            top: -45,
                            right: -105,
                            transform: "rotate(25deg)",
                            background:
                              "linear-gradient(90deg, transparent, rgba(255,255,255,.28), transparent)",
                            transition: "right .45s",
                          },
                        }}
                      >
                        <Box
                          aria-hidden="true"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            color: "#ffe76a",
                            zIndex: 0,
                          }}
                        >
                          <RedeemRoundedIcon
                            sx={{
                              position: "absolute",
                              right: 15,
                              bottom: 10,
                              fontSize: 44,
                              opacity: 0.1,
                              transform: "rotate(-9deg)",
                            }}
                          />
                          <CelebrationRoundedIcon
                            sx={{
                              position: "absolute",
                              right: 72,
                              top: 13,
                              fontSize: 24,
                              opacity: 0.08,
                              transform: "rotate(12deg)",
                            }}
                          />
                          <AutoAwesomeRoundedIcon
                            sx={{
                              position: "absolute",
                              right: 50,
                              bottom: 18,
                              fontSize: 17,
                              opacity: 0.14,
                            }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.7}>
                          <AutoAwesomeRoundedIcon
                            sx={{ fontSize: 14, color: "#fff3ad" }}
                          />
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: 1.6,
                              opacity: 0.92,
                            }}
                          >
                            LUCKY SCRATCH
                          </Typography>
                        </Box>
                        <Typography
                          fontWeight={900}
                          fontSize={29}
                          mt={1}
                          lineHeight={1}
                          letterSpacing={0.4}
                        >
                          <Typography
                            component="span"
                            sx={{
                              mr: 0.35,
                              fontSize: 21,
                              fontWeight: 800,
                              color: "#ffd500",
                            }}
                          >
                            ₹
                          </Typography>
                          {Number(group.ticketPrice).toLocaleString("en-IN")}
                        </Typography>
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                            mt: 1.3,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.25,
                          }}
                        >
                          Choose ticket{" "}
                          <ArrowForwardRoundedIcon
                            className="ticket-arrow"
                            sx={{ fontSize: 15, transition: ".2s" }}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>
    );
  }

  // Screen 2: card grid for the selected ticket price
  const cardCount = ticketsRemaining;
  const selectedGroupIndex = Math.max(
    groups.findIndex((g) => g._id === selectedGroup._id),
    0,
  );
  const selectedColors =
    TICKET_COLORS[selectedGroupIndex % TICKET_COLORS.length];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        pb: { xs: 0, sm: 4 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1, sm: 2 },
          p: { xs: 1.5, sm: 2.5 },
          mb: 2.5,
          borderRadius: { xs: 3, md: 4 },
          color: "#fff",
          background: `radial-gradient(circle at 85% 10%, rgba(255,213,0,.2), transparent 24%), linear-gradient(125deg, ${selectedColors.from}, ${selectedColors.to})`,
          boxShadow: "0 14px 35px rgba(50, 20, 75, .2)",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={{ xs: 0.8, sm: 1.2 }}
          minWidth={0}
          flex={1}
        >
          <Box
            onClick={handleBackToGroups}
            sx={{
              width: { xs: 34, sm: 38 },
              height: { xs: 34, sm: 38 },
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,.18)",
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(255,255,255,.3)" },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <Typography
              sx={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: { xs: 16, sm: 27 },
                fontWeight: 900,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
              }}
            >
              <CurrencyRupeeRoundedIcon
                sx={{
                  fontSize: { xs: 16, sm: 26 },
                  mr: 0.1,
                }}
              />
              {Number(selectedGroup.ticketPrice).toLocaleString("en-IN")}{" "}
              Scratch Cards
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: { xs: 9, sm: 12 },
                opacity: 0.84,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Tap any card below, then scratch to reveal your prize.
            </Typography>
          </Box>
        </Box>
        {canUseAutoGameWallet && (
          <Box
            onClick={() => {
              setPreferredDepositWalletType("autoGame");
              setAddPointOpen(true);
            }}
            sx={{
              ml: { xs: 4.9, sm: "auto" },
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.9, sm: 1.05 },
              px: { xs: 1, sm: 1.15 },
              py: { xs: 0.8, sm: 0.75 },
              width: { xs: "calc(100% - 39px)", sm: "auto" },
              minWidth: { sm: 264 },
              justifyContent: "space-between",
              borderRadius: { xs: 2.4, sm: 2.35 },
              border: "1px solid rgba(255,255,255,0.24)",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.1))",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.2), 0 12px 24px rgba(5,8,35,0.18)",
              backdropFilter: "blur(8px)",
              cursor: "pointer",
              transition: "transform 160ms ease, background 160ms ease",
              "&:active": {
                transform: "scale(0.985)",
              },
            }}
          >
            <Box
              sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: { xs: 2.2, sm: 2 },
                color: "#1b2055",
                background: "#ffd80a",
                boxShadow: "0 8px 16px rgba(0,0,0,.16)",
              }}
            >
              <AccountBalanceWalletRoundedIcon
                sx={{ fontSize: { xs: 24, sm: 23 } }}
              />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: { xs: 0.75, sm: 1.25 },
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.82)",
                  fontSize: { xs: 10, sm: 12 },
                  fontWeight: 800,
                  lineHeight: 1,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  letterSpacing: 0,
                  flexShrink: 0,
                }}
              >
                Auto Game Balance
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 18, sm: 23 },
                  fontWeight: 950,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: { xs: 0.15, sm: 0.25 },
                }}
              >
                <CurrencyRupeeRoundedIcon
                  sx={{ fontSize: { xs: 17, sm: 21 } }}
                />
                <Box component="span">
                  {Number(playableBalance || 0).toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}
                </Box>
              </Typography>
            </Box>
            <Box
              sx={{
                width: "1px",
                alignSelf: "stretch",
                background: "rgba(255,255,255,.18)",
                mx: 0.25,
              }}
            />
            <ChevronRightRoundedIcon
              sx={{
                color: "rgba(255,255,255,.82)",
                fontSize: { xs: 25, sm: 28 },
                flexShrink: 0,
              }}
            />
          </Box>
        )}
      </Box>

      {cardCount === 0 ? (
        <Box
          sx={{
            py: 7,
            textAlign: "center",
            borderRadius: 4,
            bgcolor: "#faf8fd",
            border: "1px dashed #d8cbea",
          }}
        >
          <ConfirmationNumberRoundedIcon
            sx={{ fontSize: 44, color: "#b7a6c9" }}
          />
          <Typography color="text.secondary" fontWeight={700} mt={1}>
            All cards in this group have been scratched.
          </Typography>
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TouchAppRoundedIcon sx={{ color: selectedColors.to }} />
            <Typography fontWeight={800} color="#2d1a40">
              Pick your lucky card
            </Typography>
          </Box>
          <Box
            onScroll={handleCardScroll}
            sx={{
              maxHeight: {
                xs: "calc(100dvh - 214px)",
                sm: "calc(70vh - 40px)",
              },
              overflowY: "auto",
              overflowX: "hidden",
              px: 0.5,
              pb: 2,
              mb: { xs: 2, sm: 0 },
              scrollPaddingBottom: { xs: 16, sm: 0 },
              overscrollBehavior: "contain",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(32,41,99,0.42) transparent",
              "&::-webkit-scrollbar": {
                width: 7,
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                minHeight: 44,
                borderRadius: 999,
                background:
                  "linear-gradient(180deg, rgba(68,80,142,.58), rgba(24,33,85,.72))",
                border: "2px solid transparent",
                backgroundClip: "padding-box",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background:
                  "linear-gradient(180deg, rgba(68,80,142,.78), rgba(24,33,85,.9))",
                backgroundClip: "padding-box",
              },
              "&::-webkit-scrollbar-button": {
                display: "none",
                width: 0,
                height: 0,
              },
            }}
          >
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {Array.from({
                length: Math.min(visibleCount, cardCount),
              }).map((_, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <Box
                    onClick={() =>
                      handlePlayCard(getPhotoUrl(cardDesigns[index]))
                    }
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      width: "100%",
                      aspectRatio: "1.0 / 1",
                      borderRadius: "12px",
                      border: "none",
                      background: "#111744",
                      overflow: "hidden",
                      boxShadow: "none",
                      transition: "transform .22s ease, box-shadow .22s ease",
                      "&:hover": {
                        transform: "translateY(-6px) scale(1.025)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <ScratchCardCover
                      key={getPhotoUrl(cardDesigns[index]) || "default"}
                      image={getPhotoUrl(cardDesigns[index])}
                    />
                    <Box
                      sx={{
                        display: "none",
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      <Dialog
        open={scratchModalOpen}
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        BackdropProps={{
          sx: {
            backgroundColor: { xs: "rgba(8,12,34,0.7)", sm: "rgba(0,0,0,0.5)" },
            backdropFilter: { xs: "blur(7px)", sm: "none" },
          },
        }}
        PaperProps={{
          sx: {
            position: "relative",
            borderRadius: { xs: 0, sm: 4 },
            overflow: "hidden",
            boxShadow: { xs: "none", sm: 24 },
            background: {
              xs: "transparent",
              sm: "linear-gradient(180deg, #fff3e0, #ffffff 40%)",
            },
          },
        }}
      >
        <Box
          component="canvas"
          ref={confettiCanvasRef}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 5,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 6,
            background: { xs: "transparent", sm: selectedColors.main },
            color: "#fff",
            py: { xs: 2.5, sm: 2 },
            textAlign: "center",
          }}
        >
          <Typography
            fontWeight="bold"
            fontSize={{ xs: 14, sm: 18 }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: { xs: 1.8, sm: 0 },
              py: { xs: 0.85, sm: 0 },
              borderRadius: { xs: 999, sm: 0 },
              backgroundColor: { xs: "rgba(21,29,85,0.72)", sm: "transparent" },
              border: { xs: "1px solid rgba(255,255,255,0.12)", sm: "none" },
              backdropFilter: { xs: "blur(12px)", sm: "none" },
              boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.18)", sm: "none" },
            }}
          >
            🎟️ ₹{Number(selectedGroup.ticketPrice).toLocaleString("en-IN")}{" "}
            Scratch Card
          </Typography>
          {(!scratchStarted || scratchRevealed) && (
            <IconButton
              aria-label="Close scratch card"
              onClick={handleScratchDialogClose}
              disabled={cancelling}
              sx={{
                position: "absolute",
                top: { xs: 20, sm: "50%" },
                right: { xs: 18, sm: 12 },
                transform: { xs: "none", sm: "translateY(-50%)" },
                width: { xs: 42, sm: "auto" },
                height: { xs: 42, sm: "auto" },
                color: "#fff",
                backgroundColor: {
                  xs: "rgba(255,255,255,0.14)",
                  sm: "rgba(255,255,255,0.1)",
                },
                border: { xs: "1px solid rgba(255,255,255,0.16)", sm: "none" },
                backdropFilter: { xs: "blur(12px)", sm: "none" },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}
        </Box>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: { xs: "center", sm: "flex-start" },
            flex: { xs: 1, sm: "initial" },
            p: { xs: 2.5, sm: 4 },
            pt: { xs: 0, sm: 4 },
          }}
        >
          {reservationId && (
            <>
              <Box
                sx={{
                  borderRadius: "12px",
                  boxShadow: {
                    xs: "none",
                    sm: "0px 8px 20px rgba(0,0,0,0.25)",
                  },
                  border: "none",
                  outline: "none",
                }}
              >
                <ScratchCanvas
                  // Keyed on the reservation so every card gets a brand-new
                  // canvas. The cover is drawn by an effect keyed on the
                  // image props, which are identical between cards, so a
                  // reused instance would never repaint its cover.
                  key={reservationId}
                  width={scratchCardSize}
                  height={scratchCardSize}
                  image={selectedCover || "/scratch-card-cover.png"}
                  overlayImage={
                    selectedCover ? null : "/scratch-card-icons-overlay.svg"
                  }
                  fallbackImage="/scratch-card-cover.png"
                  fallbackOverlayImage="/scratch-card-icons-overlay.svg"
                  finishPercent={75}
                  onScratchStart={handleScratchStart}
                  onComplete={handleScratchComplete}
                >
                  <Box
                    width="100%"
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={0.5}
                    sx={{
                      borderRadius: 0,
                      overflow: "hidden",
                      background:
                        scratchRevealed && scratchResult
                          ? scratchResult.didWin
                            ? "radial-gradient(circle at 50% 8%, #fffef8 0%, #fff5c9 52%, #ffe49b 100%)"
                            : "radial-gradient(circle at 50% 8%, #ffffff 0%, #f5f7fa 55%, #e8ecf2 100%)"
                          : "radial-gradient(circle at 50% 25%, #ffffff 0%, #f5f7fa 58%, #e9edf3 100%)",
                      border: "none",
                      boxShadow: "none",
                      animation: "none",
                    }}
                  >
                    {scratchRevealed && scratchResult ? (
                      <>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            color: scratchResult.didWin ? "#a66d00" : "#68758b",
                            backgroundColor: scratchResult.didWin
                              ? "rgba(255,193,7,0.16)"
                              : "rgba(100,113,137,0.09)",
                            border: scratchResult.didWin
                              ? "1px solid rgba(190,137,0,0.1)"
                              : "1px solid rgba(89,103,128,0.08)",
                            boxShadow: scratchResult.didWin
                              ? "0 7px 18px rgba(183,126,0,0.12)"
                              : "0 8px 20px rgba(63,74,94,0.1)",
                          }}
                        >
                          {scratchResult.didWin ? (
                            <CelebrationRoundedIcon fontSize="medium" />
                          ) : (
                            <SentimentDissatisfiedRoundedIcon fontSize="medium" />
                          )}
                        </Box>
                        <Typography
                          fontWeight={900}
                          fontSize={9.5}
                          textAlign="center"
                          letterSpacing={1.8}
                          color={scratchResult.didWin ? "#996500" : "#7b879c"}
                        >
                          {scratchResult.didWin ? "YOU WON" : "TRY AGAIN"}
                        </Typography>
                        <Typography
                          fontWeight={900}
                          fontSize={scratchResult.didWin ? 24 : 14}
                          lineHeight={1.05}
                          textAlign="center"
                          px={1.5}
                          color={scratchResult.didWin ? "#1c2555" : "#354056"}
                          letterSpacing={scratchResult.didWin ? -0.4 : -0.25}
                        >
                          {scratchResult.didWin
                            ? `₹${Number(scratchResult.winAmount).toLocaleString("en-IN")}`
                            : "Better luck next time"}
                        </Typography>
                      </>
                    ) : revealPending ? (
                      <>
                        <CircularProgress size={26} sx={{ color: "#6672a5" }} />
                        <Typography
                          fontWeight={900}
                          fontSize={11}
                          letterSpacing={1.35}
                          color="#303b6f"
                        >
                          REVEALING
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "14px",
                            display: "grid",
                            placeItems: "center",
                            color: "#6672a5",
                            background:
                              "linear-gradient(145deg, #ffffff, #e8ebf3)",
                            border: "1px solid rgba(32,41,99,0.1)",
                            boxShadow: "0 8px 18px rgba(28,37,85,0.12)",
                          }}
                        >
                          <AutoAwesomeRoundedIcon fontSize="small" />
                        </Box>
                        <Typography
                          fontWeight={900}
                          fontSize={11}
                          letterSpacing={1.35}
                          color="#303b6f"
                        >
                          KEEP SCRATCHING
                        </Typography>
                        <Typography
                          fontWeight={600}
                          fontSize={9}
                          color="#8b93a5"
                        >
                          Your surprise is underneath
                        </Typography>
                      </>
                    )}
                  </Box>
                </ScratchCanvas>
              </Box>

              {!scratchRevealed && (
                <Box
                  sx={{
                    mt: { xs: 3.5, sm: 2 },
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    color: {
                      xs: "rgba(255,255,255,0.82)",
                      sm: "text.secondary",
                    },
                  }}
                >
                  {isMobile && (
                    <TouchAppRoundedIcon sx={{ fontSize: 15, opacity: 0.78 }} />
                  )}
                  <Typography
                    fontSize={{ xs: 12.5, sm: 13 }}
                    fontWeight={{ xs: 600, sm: 400 }}
                    letterSpacing={{ xs: 0.45, sm: 0 }}
                    lineHeight={1.4}
                  >
                    {isMobile
                      ? "Scratch to reveal your reward"
                      : "Rub the card above to reveal your prize"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScratchAndWinGame;
